import { Button } from '@mui/material';
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { every, find } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult, useQueryClient } from 'react-query';
import * as Yup from 'yup';

import { internalTransferApi } from 'api';
import {
  CloseDialogHandler,
  CloseDialogResult,
  DataWrapper,
  Dialog,
  FormActions,
  FormControls,
  FormikNumericField,
  FormikSelect,
  FormikTextField,
  UserSelect,
} from 'components';
import {
  AppConfigArea,
  AppConfigKey,
  AssetType,
  InternalTransferDetails,
  QueryKey,
  UserRole,
} from 'enums';
import { useAppConfigValue, useCurrencies, useMutation } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Asset, Shop, User, CreateInternalTransferValues } from 'types';
import { assetUtils } from 'utils';

type Props = {
  open: boolean;
  queryResultUsers?: UseQueryResult<User[]>;
  queryResultShops?: UseQueryResult<Shop[]>;
  queryResultAssets: UseQueryResult<Asset[]>;
  onClose: CloseDialogHandler;
};

export const AdminCreateInternalTransferDialog: React.FC<Props> = ({
  open,
  queryResultUsers,
  queryResultShops,
  queryResultAssets,
  onClose,
}) => {
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.internal_transfers',
  });
  const queryClient = useQueryClient();

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        category: Yup.string().required(tCommon('errors.required')),
        amount: Yup.number()
          .positive(tCommon('errors.natural_number'))
          .required(tCommon('errors.required')),
        senderUserId: Yup.string()
          .required(tCommon('errors.required'))
          .uuid(tCommon('errors.invalid')),
        senderAssetId: Yup.string()
          .required(tCommon('errors.required'))
          .uuid(tCommon('errors.invalid')),
        recipientUserId: Yup.string()
          .required(tCommon('errors.required'))
          .uuid(tCommon('errors.invalid')),
        recipientAssetId: Yup.string().uuid(tCommon('errors.invalid')),
        comment: Yup.string().notRequired(),
      }),
    [tCommon],
  );

  const initialValues: CreateInternalTransferValues = useMemo(
    () => ({
      category: '',
      senderUserId: '',
      senderAssetId: '',
      amount: 0,
      recipientUserId: '',
      recipientAssetId: '',
    }),
    [],
  );

  const { mutate: create, isLoading: isCreateLoading } = useMutation(
    internalTransferApi.createAdmin,
    {
      requireSecureConfirm: true,
    },
  );
  const isLoading = useMemo(
    () =>
      queryResultUsers?.isLoading ||
      queryResultShops?.isLoading ||
      isCreateLoading,
    [queryResultUsers, queryResultShops, isCreateLoading],
  );

  const handleClose = useCallback(
    (
      result: CloseDialogResult,
      helpers: FormikHelpers<CreateInternalTransferValues>,
    ) => {
      helpers.resetForm();
      onClose(result);
    },
    [onClose],
  );

  const handleSubmit = useCallback(
    (
      values: CreateInternalTransferValues,
      helpers: FormikHelpers<CreateInternalTransferValues>,
    ) => {
      const senderAsset = find(queryResultAssets.data, {
        id: values.senderAssetId,
      });
      const recipientAsset = find(queryResultAssets.data, {
        id: values.recipientAssetId,
      });

      create(
        {
          category: values.category,
          senderUserId: values.senderUserId,
          senderAssetId: values.senderAssetId,
          amount: +values.amount,
          recipientUserId: values.recipientUserId,
          recipientAssetId: values.recipientAssetId,
          ...(senderAsset?.type === AssetType.InsuranceDeposit && {
            details: InternalTransferDetails.DepositToBalance,
          }),
          ...(recipientAsset?.type === AssetType.InsuranceDeposit && {
            details: InternalTransferDetails.BalanceToDeposit,
          }),
          comment: values.comment,
        },
        {
          onSettled: () => {
            helpers.setSubmitting(false);
          },
          onSuccess: () => {
            queryClient.invalidateQueries(QueryKey.Assets);
            queryClient.invalidateQueries(QueryKey.InternalTransfers);

            handleClose({ ok: true }, helpers);
          },
        },
      );
    },
    [create, queryClient, handleClose, queryResultAssets.data],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const { values, setFieldError } = formik;

  const getAssets = useCallback(
    (userId: string, currencyId?: string) =>
      assetUtils.filterAssets(
        queryResultAssets.data,
        { userId, assetCurrencyId: currencyId },
        queryResultShops?.data,
      ),
    [queryResultAssets.data, queryResultShops?.data],
  );

  const senderAssets = useMemo(
    () => getAssets(values.senderUserId),
    [getAssets, values.senderUserId],
  );

  const senderAssetOptions = useMemo(
    () => assetUtils.getAssetOptions(senderAssets, UserRole.Admin),
    [senderAssets],
  );

  const selectedSenderAsset = useMemo(
    () => find(senderAssets, { id: values.senderAssetId }),
    [senderAssets, values.senderAssetId],
  );

  const recipientAssets = useMemo(
    () =>
      getAssets(values.recipientUserId, selectedSenderAsset?.assetCurrencyId),
    [getAssets, values.recipientUserId, selectedSenderAsset],
  );

  const recipientNoAssetCreated = useMemo(
    () =>
      !recipientAssets.length ||
      (recipientAssets.length === 1 &&
        every(recipientAssets, (asset) => !asset.id)),
    [recipientAssets],
  );

  const recipientAssetOptions = useMemo(
    () => assetUtils.getAssetOptions(recipientAssets, UserRole.Admin),
    [recipientAssets],
  );

  const { getAssetCurrencySymbol } = useCurrencies();
  const getAssetCurrencySymbolByAssetId = useCallback(
    (assetId: string, assets: Asset[]) => {
      const selectedAsset = find(assets, { id: assetId });
      return getAssetCurrencySymbol(selectedAsset?.assetCurrencyId);
    },
    [getAssetCurrencySymbol],
  );

  const categoriesConfigQueryResult = useAppConfigValue({
    area: AppConfigArea.Platform,
    key: AppConfigKey.InternalTransferCategories,
  });

  const categoriesOptions = useMemo(
    () =>
      Object.entries((categoriesConfigQueryResult?.data as any) || {}).map(
        ([value, label]) => ({ value, label: label as string }),
      ),
    [categoriesConfigQueryResult],
  );

  // TODO: no highlight, only with hot reload
  useEffect(() => {
    if (selectedSenderAsset && selectedSenderAsset.balance < values.amount) {
      setFieldError('amount', tCommon('errors.not_enough_money'));
    }
  }, [selectedSenderAsset, tCommon, values.amount, setFieldError]);

  return (
    <FormikProvider value={formik}>
      <Dialog
        open={open}
        title={t('create_admin_modal.title')}
        data={{ values, formikHelpers: formik }}
        onClose={(result) => handleClose(result, formik)}
        modal
      >
        <DataWrapper isLoading={isLoading}>
          <Form>
            <FormControls>
              <FormikSelect
                options={categoriesOptions}
                label={t('create_admin_modal.category')}
                className="tw-w-full"
                name="category"
              />
              <UserSelect
                name="senderUserId"
                fullWidth
                label={t('create_admin_modal.sender.user')}
                users={queryResultUsers?.data}
              />
              <FormikSelect
                label={t('create_admin_modal.sender.asset')}
                name="senderAssetId"
                options={senderAssetOptions}
                disabled={!values.senderUserId}
              />
              <FormikNumericField
                label={t('fields.amount')}
                name="amount"
                allowNegative={false}
                disabled={!values.senderAssetId}
                suffix={getAssetCurrencySymbolByAssetId(
                  values.senderAssetId,
                  senderAssets,
                )}
              />
              <UserSelect
                name="recipientUserId"
                fullWidth
                label={t('create_admin_modal.recipient.user')}
                users={queryResultUsers?.data}
              />
              {!recipientNoAssetCreated && (
                <FormikSelect
                  label={t('create_admin_modal.recipient.asset')}
                  name="recipientAssetId"
                  options={recipientAssetOptions}
                  disabled={!values.recipientUserId}
                />
              )}
              <FormikTextField
                label={t('create_admin_modal.comment')}
                name="comment"
                className="tw-w-full"
                multiline
                rows={3}
              />
            </FormControls>
            <FormActions>
              <Button
                color="primary"
                variant="contained"
                type="submit"
                disabled={isLoading || formik.isSubmitting}
              >
                {tCommon('buttons.create')}
              </Button>
            </FormActions>
          </Form>
        </DataWrapper>
      </Dialog>
    </FormikProvider>
  );
};
